import { createStore } from 'zustand/vanilla';
import type { InventoryEntry } from '@stageplottter/core';
import { isApiError } from '../../api/client';
import { inventoryApi } from './api';
import type { Inventory } from './api';

export type InventoryStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface InventoryState {
  ws: string | null;
  status: InventoryStatus;
  revision: number;
  entries: InventoryEntry[];
  updatedAt: string | null;
  updatedBy: string | null;
  /** Local edits not yet saved. */
  dirty: boolean;
  saving: boolean;
  /** Set when a save hit a newer server version (the list was reloaded). */
  conflict: boolean;
  /** Loads the inventory of `ws` (again with `force`). */
  load(ws: string, force?: boolean): Promise<void>;
  /** Replaces the entries locally and schedules saving. */
  edit(fn: (entries: InventoryEntry[]) => InventoryEntry[]): void;
  /** Saves pending edits now. */
  flush(): Promise<void>;
  dismissConflict(): void;
}

type Api = Pick<ReturnType<typeof inventoryApi>, 'get' | 'put'>;

const SAVE_DELAY_MS = 600;

/**
 * Workspace inventory shared by the inventory page and the material list (decision U15). Edits
 * are saved debounced with the base revision; a 409 reloads the newer list and flags a conflict.
 * Guests and non-members get `unavailable` (no inventory columns).
 */
export function createInventoryStore(getApi: () => Api = inventoryApi) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  return createStore<InventoryState>()((set, get) => {
    const adopt = (inv: Inventory) =>
      set({
        status: 'ready',
        revision: inv.revision,
        entries: inv.entries,
        updatedAt: inv.updatedAt,
        updatedBy: inv.updatedBy,
      });

    const save = async (): Promise<void> => {
      const { ws, dirty, revision, entries } = get();
      if (ws === null || !dirty) return;
      set({ saving: true, dirty: false });
      let again = false;
      try {
        const saved = await getApi().put(ws, { revision, entries });
        // edits made while saving stay local (dirty) and go out with the new revision
        set({
          status: 'ready',
          revision: saved.revision,
          updatedAt: saved.updatedAt,
          updatedBy: saved.updatedBy,
        });
        again = get().dirty;
        if (!again) set({ entries: saved.entries });
      } catch (err) {
        if (isApiError(err, 409)) {
          set({ conflict: true, dirty: false });
          adopt(await getApi().get(ws));
        } else {
          // retried with the next edit (or flush), not in a loop
          set({ dirty: true, status: 'error' });
        }
      } finally {
        set({ saving: false });
      }
      if (again) await save();
    };

    const run = (): Promise<void> => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      inFlight ??= save().finally(() => {
        inFlight = null;
      });
      return inFlight;
    };

    return {
      ws: null,
      status: 'idle',
      revision: 0,
      entries: [],
      updatedAt: null,
      updatedBy: null,
      dirty: false,
      saving: false,
      conflict: false,
      load: async (ws, force = false) => {
        const s = get();
        if (!force && s.ws === ws && (s.status === 'ready' || s.status === 'loading')) return;
        set({ ws, status: 'loading', entries: [], revision: 0, dirty: false, conflict: false });
        try {
          const inv = await getApi().get(ws);
          if (get().ws === ws) adopt(inv);
        } catch (err) {
          if (get().ws !== ws) return;
          set({
            status:
              isApiError(err, 403) || isApiError(err, 404) || isApiError(err, 401)
                ? 'unavailable'
                : 'error',
          });
        }
      },
      edit: (fn) => {
        set({ entries: fn(get().entries), dirty: true });
        if (timer !== null) clearTimeout(timer);
        timer = setTimeout(() => void run(), SAVE_DELAY_MS);
      },
      flush: () => run(),
      dismissConflict: () => set({ conflict: false }),
    };
  });
}

export const inventoryStore = createInventoryStore();
